import {
  FileTextOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { Button, Card, Tag, Tooltip } from 'antd';
import { usePostApi } from 'hooks/api';
import { Link } from 'react-router-dom';
import { formatDate, formatDateTime } from 'utils/formatDate';

import {
  employeeProgramStatus,
  employeeProgramStatusCanceling,
  employeeProgramStatusColors,
  employeeProgramStatusCompleted,
  employeeProgramStatusInProgress,
  employeeProgramStatusPending,
  employeeProgramStatusVesting,
} from 'apps/investment/constants/employeeProgram';
import InfoCard from 'common/ui/info-card';
import UIIconTooltip from 'common/ui/UIIconTooltip';
import { Info } from 'lucide-react';
import { formatCurrency } from 'utils/formatAmount';
import { safeFormatDate } from 'utils/safe-format-date';
import { accountRequirements, programRequirements } from './constants';
import EstimateVesting from './EstimateVesting';
import ProgramDetailPortfolio from './portfolio-detail';
import RequirementList from './program-requirement-list';

const ItemDetail = ({ item, apiCall, onRefresh }: any) => {
  const {
    code,
    company_investment_detail,
    employee_investment_detail,
    company,
    employee,
    ...data
  } = item;

  const { id: companyId, name } = company || {};
  const { id: employeeId, full_name } = employee || {};

  const {
    id,
    status,
    can_vesting,
    can_complete,
    can_terminate,
    can_cancel,
    required_active_conditions,

    company_contribute_amount,
    employee_contribute_amount,
    sum_company_contributed_amount,
    sum_employee_contributed_amount,
    monthly_order_payment,
    investment_interval,

    start_date,
    end_date,
    created_at,
    updated_at,
    canceled_at,
    canceling_at,
    completed_at,
    vesting_at,
    joined_at,
  } = data || {};

  // Every mutation here is a program state transition (invite / vesting /
  // terminate / complete / cancel / inProgress / pending) that changes the
  // status fields rendered below, so each one refetches the detail query.
  const refreshOnSuccess = () => onRefresh?.();

  const { post, isLoading: isSubmitting } = usePostApi(apiCall.invite, {
    resourceName: 'data',
    onSuccess: refreshOnSuccess,
  });

  const { post: postVesting, isLoading: isSubmittingVesting } = usePostApi(
    apiCall.vesting,
    {
      resourceName: 'data',
      onSuccess: refreshOnSuccess,
    }
  );

  const { post: postTerminate, isLoading: isSubmittingTerminate } = usePostApi(
    apiCall.terminate,
    {
      resourceName: 'data',
      onSuccess: refreshOnSuccess,
    }
  );

  const { post: postComplete, isLoading: isSubmittingComplete } = usePostApi(
    apiCall.complete,
    {
      resourceName: 'data',
      onSuccess: refreshOnSuccess,
    }
  );

  const { post: postCancel, isLoading: isSubmittingCancel } = usePostApi(
    apiCall.cancel,
    {
      resourceName: 'data',
      onSuccess: refreshOnSuccess,
    }
  );

  const {
    post: postInProgress,
    isLoading: isSubmittingInProgress,
  } = usePostApi(apiCall.inProgress, {
    resourceName: 'data',
    onSuccess: refreshOnSuccess,
  });

  const { post: postPending, isLoading: isSubmittingPending } = usePostApi(
    apiCall.pending,
    {
      resourceName: 'data',
      onSuccess: refreshOnSuccess,
    }
  );

  const onInvite = () => {
    post({ id });
  };

  const onVesting = () => {
    postVesting({ id });
  };

  const onForceVesting = () => {
    postVesting({ id, force: true });
  };

  const onTerminate = () => {
    postTerminate({ id });
  };

  const onComplete = () => {
    postComplete({ id });
  };

  const onCancel = () => {
    postCancel({ id });
  };

  const onInProgress = () => {
    postInProgress({ id });
  };

  const onPending = () => {
    postPending({ id });
  };

  const programActions = [
    {
      label: 'Invite',
      onClick: onInvite,
      loading: isSubmitting,
      disabled: status !== employeeProgramStatusPending,
      confirmMessage: '',
      showButton: true,
      buttonLabel: 'Invite',
    },
    {
      label: 'Trigger Vesting',
      onClick: onVesting,
      loading: isSubmittingVesting,
      disabled: !can_vesting,
      confirmMessage:
        'Confirm to vesting program? This action can not rollback',
      showButton: status == employeeProgramStatusInProgress,
      buttonLabel: 'Vesting',
    },
    {
      label: 'Force Vesting',
      onClick: onForceVesting,
      loading: isSubmittingVesting,
      disabled: status !== employeeProgramStatusInProgress,
      confirmMessage:
        'This vests the program BEFORE its scheduled end date, using only the intervals contributed so far. This action can not rollback. Are you sure?',
      showButton: status == employeeProgramStatusInProgress && !can_vesting,
      isDanger: true,
      buttonLabel: 'Force Vesting',
    },
    {
      label: 'Trigger Complete',
      onClick: onComplete,
      loading: isSubmittingComplete,
      disabled: !can_complete,
      confirmMessage:
        'Confirm to complete program? This action can not rollback',
      showButton: status == employeeProgramStatusVesting,
      buttonLabel: 'Complete',
    },
    {
      label: 'Trigger Terminate',
      onClick: onTerminate,
      loading: isSubmittingTerminate,
      disabled: !can_terminate,
      confirmMessage:
        'Confirm to terminate program? This action can not rollback',
      showButton:
        status == employeeProgramStatusPending ||
        status == employeeProgramStatusInProgress,
      isDanger: true,
      buttonLabel: 'Terminate',
    },
    {
      label: 'Trigger Cancel',
      onClick: onCancel,
      loading: isSubmittingCancel,
      disabled: !can_cancel,
      confirmMessage: 'Confirm to cancel program? This action can not rollback',
      showButton: status == employeeProgramStatusCanceling,
      isDanger: true,
      buttonLabel: 'Cancel',
    },
    {
      label: 'Trigger Active',
      onClick: onInProgress,
      loading: isSubmittingInProgress,
      disabled:
        required_active_conditions && required_active_conditions.length > 0,
      confirmMessage:
        'Confirm to switch program to active? This action can not rollback',
      showButton: status == employeeProgramStatusPending,
      warningMessage:
        required_active_conditions && required_active_conditions.length > 0
          ? 'Chưa đủ điều kiện'
          : '',
      buttonLabel: 'Active Program',
    },
    {
      label: 'Trigger Pending',
      onClick: onPending,
      loading: isSubmittingPending,
      disabled: false,
      confirmMessage:
        'Confirm to switch program to pending? This action can not rollback',
      showButton: status == employeeProgramStatusInProgress,
      isDanger: true,
      buttonLabel: 'Inactive Program',
    },
  ];

  const showEstimateVesting =
    status == employeeProgramStatusVesting ||
    status == employeeProgramStatusCompleted;

  return (
    <>
      <Card className="my-4 shadow-md overflow-hidden ">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-end gap-2">
              <Tag
                style={{
                  fontSize: 'large',
                  fontWeight: 'bold',
                  padding: '8px',
                }}
                color={employeeProgramStatusColors[status]}
              >
                {employeeProgramStatus[status]}
              </Tag>
              <Link to={`/futureflex/program-orders?employee_program_id=${id}`}>
                <UIIconTooltip
                  title="Xem danh sách lệnh"
                  Icon={FileTextOutlined}
                  size="1.75rem"
                />
              </Link>
              <Link to={`/futureflex/employee-programs/${id}/portfolio`}>
                <UIIconTooltip
                  title="Xem danh mục đầu tư"
                  Icon={PieChartOutlined}
                  size="1.75rem"
                />
              </Link>
              <Link to={`/futureflex/employee-programs/${id}/investment-plans`}>
                <UIIconTooltip
                  title="Xem kế hoạch đầu tư"
                  Icon={LineChartOutlined}
                  size="1.75rem"
                />
              </Link>
            </div>

            <div className="flex gap-2">
              {programActions
                ?.filter(({ showButton }) => showButton)
                .map(
                  ({
                    label,
                    onClick,
                    loading,
                    disabled,
                    confirmMessage,
                    isDanger,
                    buttonLabel,
                    warningMessage,
                  }) => (
                    <div className="flex gap-1" key={label}>
                      <Button
                        size="large"
                        disabled={disabled}
                        loading={loading}
                        type={'primary'}
                        danger={isDanger}
                        onClick={() => confirm(confirmMessage) && onClick()}
                      >
                        {buttonLabel}
                      </Button>
                      {warningMessage && (
                        <Tooltip title={warningMessage}>
                          <InfoCircleOutlined
                            style={{ color: '#b8860b', fontSize: '20px' }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )
                )}
            </div>
          </div>

          {status == employeeProgramStatusPending && (
            <div className="space-y-6">
              <RequirementList
                title="A) Yêu cầu về chi tiết chương trình:"
                items={programRequirements}
                required_active_conditions={required_active_conditions}
              />

              <RequirementList
                title="B) Yêu cầu về tài khoản:"
                items={accountRequirements}
                required_active_conditions={required_active_conditions}
              />
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoCard
              label="Nhân viên"
              value={
                <Link
                  to={`/futureflex/employees/${employeeId}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {full_name}
                </Link>
              }
            />
            <InfoCard
              label="Công ty"
              value={
                <Link
                  to={`/futureflex/companies/${companyId}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {name}
                </Link>
              }
            />
            <InfoCard label="Mã chương trình" value={code} />
          </div>

          <h3 className="text-base font-medium gap-2 flex items-center">
            <Info className="text-primary" />
            Thông tin chuơng trình
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              className="text-right"
              label="Ngày giao dịch hằng tháng"
              value={monthly_order_payment}
            />
            <InfoCard
              className="text-right"
              label="Số kỳ"
              value={investment_interval}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              className="text-right"
              label="Công ty góp định kỳ"
              value={formatCurrency(company_contribute_amount)}
            />
            <InfoCard
              className="text-right"
              label="Nhân viên góp định kỳ"
              value={formatCurrency(employee_contribute_amount)}
            />
            <InfoCard
              className="text-right"
              label="Công ty đã góp"
              value={formatCurrency(sum_company_contributed_amount)}
            />
            <InfoCard
              className="text-right"
              label="Nhân viên đã góp"
              value={formatCurrency(sum_employee_contributed_amount)}
            />
          </div>
        </div>

        <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <InfoCard
              label="Start Date"
              value={safeFormatDate(start_date, formatDate)}
            />
            <InfoCard
              label="End Date"
              value={safeFormatDate(end_date, formatDate)}
            />
            <InfoCard
              label="Created At"
              value={safeFormatDate(created_at, formatDateTime)}
            />
            <InfoCard
              label="Updated At"
              value={safeFormatDate(updated_at, formatDateTime)}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <InfoCard
              label="Joined At"
              value={safeFormatDate(joined_at, formatDateTime)}
            />
            <InfoCard
              label="Vesting At"
              value={safeFormatDate(vesting_at, formatDateTime)}
            />
            <InfoCard
              label="Completed At"
              value={safeFormatDate(completed_at, formatDateTime)}
            />
            <InfoCard
              label="Canceled At"
              value={safeFormatDate(canceled_at, formatDateTime)}
            />
            <InfoCard
              label="Terminate At"
              value={safeFormatDate(canceling_at, formatDateTime)}
            />
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        <ProgramDetailPortfolio />

        {showEstimateVesting && (
          <EstimateVesting
            id={id}
            companyId={companyId}
            employeeId={employeeId}
            apiCall={apiCall}
            programStatus={status}
          />
        )}
      </div>
    </>
  );
};

export default ItemDetail;
